import React, { useState, useEffect } from "react";
import { Card, Button, Modal, Form, Alert, Spinner, Badge } from "react-bootstrap";
import Cookies from "js-cookie";
import axios from "axios";
import CardViewTable from '../../../components/CardViewTable/CardViewTable';
import { API_BASE_URL as API_BASE } from "../../../config";

function GerenciarServicos() {
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentServico, setCurrentServico] = useState(null);
  const [nome, setNome] = useState("");
  const [preco, setPreco] = useState("");
  const [duracaoMinutos, setDuracaoMinutos] = useState("");
  const [imagemUrl, setImagemUrl] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [clienteMasterId, setClienteMasterId] = useState(null);

  useEffect(() => {
    const usuario = Cookies.get("usuario") ? JSON.parse(Cookies.get("usuario")) : null;
    if (usuario && usuario.role === "ClienteMaster") {
      setClienteMasterId(usuario.id);
      fetchServicos(usuario.id);
    } else {
      setError("Acesso negado. Você não é um administrador.");
      setLoading(false);
    }
  }, []);

  const fetchServicos = async (masterId) => {
    setLoading(true);
    setError(null);
    try {
      const token = Cookies.get("authToken");
      const response = await axios.get(`${API_BASE}/Servicos/admin?idClienteMaster=${masterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServicos(response.data);
    } catch (err) {
      console.error(err);
      setError("Erro ao carregar serviços.");
    } finally {
      setLoading(false);
    }
  };

  const handleShowAddModal = () => {
    setIsEditing(false);
    setCurrentServico(null);
    setNome("");
    setPreco("");
    setDuracaoMinutos("");
    setImagemUrl("");
    setSelectedFile(null);
    setAtivo(true);
    setShowModal(true);
  };

  const handleShowEditModal = (servico) => {
    setIsEditing(true);
    setCurrentServico(servico);
    setNome(servico.nome);
    setPreco(servico.preco);
    setDuracaoMinutos(servico.duracaoMinutos);
    setImagemUrl(servico.imagemUrl || "");
    setSelectedFile(null);
    setAtivo(servico.ativo);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const uploadImage = async (servicoId = null) => {
    if (!selectedFile) return imagemUrl;

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const token = Cookies.get("authToken");
      const url = servicoId
        ? `${API_BASE}/Servicos/${servicoId}/upload-image`
        : `${API_BASE}/Servicos/upload-image`; // Se criar novo, o upload será feito depois de criar
      const response = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      return response.data.imagemUrl;
    } catch (err) {
      console.error("Erro ao fazer upload da imagem:", err);
      window.globalAlert("Erro ao fazer upload da imagem.", "error");
      return imagemUrl; // Retorna a URL antiga em caso de erro
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");

      // Dados do serviço
      const servicoData = {
        nome,
        preco: parseFloat(preco),
        duracaoMinutos: parseInt(duracaoMinutos),
        imagemUrl, // URL inicial
        ativo,
        clienteMasterId,
      };

      if (isEditing) {
        // 1️⃣ Atualiza serviço
        await axios.put(`${API_BASE}/Servicos/${currentServico.id}`, servicoData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // 2️⃣ Faz upload da imagem se tiver arquivo
        if (selectedFile) {
          const finalImagemUrl = await uploadImage(currentServico.id);
          setImagemUrl(finalImagemUrl);
        }
      } else {
        // Cria novo serviço
        const createResponse = await axios.post(`${API_BASE}/Servicos`, servicoData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const newServicoId = createResponse.data.id;

        // Faz upload da imagem se tiver arquivo
        if (selectedFile) {
          const finalImagemUrl = await uploadImage(newServicoId);
          // Atualiza a imagem do serviço criado
          await axios.put(`${API_BASE}/Servicos/${newServicoId}`, { ...servicoData, imagemUrl: finalImagemUrl }, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }

      fetchServicos(clienteMasterId);
      handleCloseModal();
    } catch (err) {
      console.error(err);
      setError("Erro ao salvar serviço.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await window.globalConfirm("Tem certeza que deseja excluir este serviço?"))) return;
    setSubmitting(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");
      await axios.delete(`${API_BASE}/Servicos/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchServicos(clienteMasterId);
    } catch (err) {
      console.error(err);
      setError("Erro ao excluir serviço.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner animation="border" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  const cardStyle = {
    backgroundColor: "#3c4049",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "25px",
    width: "100%",
    maxWidth: "100%",
    margin: "0",
  };

  return (
    <Card className="shadow-lg" style={cardStyle}>
      <Card.Title className="mb-4 fs-3 text-center">Gerenciar Serviços</Card.Title>
      <Card.Body>
        <Button variant="primary" onClick={handleShowAddModal} className="mb-3">
          Adicionar Serviço
        </Button>

        {servicos.length > 0 ? (
          <CardViewTable
            data={servicos}
            keyField="id"
            cardTitleKey="nome"
            columns={[
              { key: 'nome', header: 'Nome' },
              {
                key: 'preco',
                header: 'Preço',
                render: (servico) => `R$ ${servico.preco.toFixed(2)}`,
              },
              { key: 'duracaoMinutos', header: 'Duração (min)' },
              {
                key: 'ativo',
                header: 'Ativo',
                render: (servico) => (
                  <Badge bg={servico.ativo ? 'success' : 'danger'}>
                    {servico.ativo ? '✅ Sim' : '❌ Não'}
                  </Badge>
                ),
              },
              { key: 'actions', header: 'Ações' },
            ]}
            actions={(servico) => (
              <div className="d-flex flex-wrap gap-2">
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => handleShowEditModal(servico)}
                  className="flex-grow-1"
                >
                  Editar
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(servico.id)}
                  className="flex-grow-1"
                >
                  Excluir
                </Button>
              </div>
            )}
          />
        ) : (
          <Alert variant="info" className="text-center mb-0">
            Nenhum serviço encontrado.
          </Alert>
        )}

        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{isEditing ? "Editar Serviço" : "Adicionar Serviço"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Nome</Form.Label>
                <Form.Control type="text" value={nome} onChange={(e) => setNome(e.target.value)} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Preço</Form.Label>
                <Form.Control type="number" step="0.01" value={preco} onChange={(e) => setPreco(e.target.value)} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Duração (minutos)</Form.Label>
                <Form.Control type="number" value={duracaoMinutos} onChange={(e) => setDuracaoMinutos(e.target.value)} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Imagem do Serviço</Form.Label>
                {imagemUrl && (
                  <div className="mb-2">
                    <img
                      src={imagemUrl.startsWith("http") ? imagemUrl : `${API_BASE.replace('/api', '')}${imagemUrl}`}
                      alt="Serviço"
                      style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "10px" }}
                    />
                  </div>
                )}
                <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check type="checkbox" label="Ativo" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting ? <Spinner animation="border" size="sm" /> : "Salvar"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Card.Body>
    </Card>
  );
}

export default GerenciarServicos;
